import { useState } from 'react';
import type { ColorRule, DistrictMode } from '@citizen-walk/shared-types';
import { defaultMarkerColorForMode, mapModeLabel } from './mapDefaults';
import { useEcwLegend, type MapLegendEntry } from './useEcwLegend';

/**
 * Sprint AR (2026-05-20、R-33): 地図凡例 floating button + modal overlay。
 *
 * 旧 MapTab L599-639 `MapLegend` を v2 へ移植。docs/97 §4-1 判定:
 *   - 旧 getQuadrantLegendEntries (mapDisplay.colorRule 駆動) は schema 未確定
 *     (`displayRules: unknown`) のため L4 移植不可 → 「mode 1 色のみの fallback」へ縮小。
 *   - UX (左下 floating button / overlay modal / 色丸 + ラベル列) は旧と同等。
 *
 * 仕様:
 *   - mode が null/undefined または `mapModeLabel(mode)` が null → 凡例 button を出さない
 *     (旧 L606 「entries.length === 0 → return null」と同 UX)。
 *   - entries は現状 1 件のみ (mode-default color + mapModeLabel)。M5 displayRules で
 *     admin が colorRule (quadrant 等) を設定したら entries 多色化する想定 (TODO 下記)。
 *   - 左下に地図へ重ねた floating button (old2 MapTab L620-627 と同 UX)、
 *     overlay は `fixed inset-0` の dialog 風 modal (旧 L617-635 同等)。
 *   - close trigger: overlay click / × button click。modal 本体クリックは伝搬停止で残す。
 *   - **位置 (2026-06-03 fix)**: button は `bottom-20`。MapPage の `<main>` は BottomTabBar 分の
 *     `pb-16` (4rem) を確保しており、地図 canvas は main 下端の 4rem 上で終わる。旧実装の
 *     `bottom-4` だと地図の外 (タブバーとの隙間) に白ボタンが落ちていたため、`bottom-20`
 *     (= タブバー 4rem + 余白 1rem) で「地図の上に重なる」old2 同等の見た目へ戻す。
 *   - **アイコン表示 (2026-06-03)**: ColorRule 各行の `icon` (♥ ★ ▲ 等、`LegendEntry.icon`) を
 *     entry 色で表示し、地図マーカーと凡例の見た目を一致させる。icon 無しの行は従来どおり色丸。
 *
 * 将来 hook 追加点:
 *   - admin (M5 displayRules.colorRule) で quadrant ベース等の多色 entries に拡張する際は、
 *     `entries = getQuadrantLegendEntries(displayRules, mode)` のように差し替え。
 *     displayRules schema 確定 sprint で本ファイルの `entries` 計算箇所のみを書き換える。
 */
export interface MapLegendProps {
  /** 地区 mode。null/undefined または mapModeLabel が null なら凡例 button を表示しない。 */
  mode: DistrictMode | null | undefined;
  /**
   * 地区別 ColorRule (Sprint BG-DataSettings 6+7、U-H8 + L17)。
   * 指定があれば buckets[] / cells[] + fallback を凡例エントリ化、
   * 未指定なら mode-default 色 + mapModeLabel の 1 件 fallback (旧 UX 互換)。
   */
  colorRule?: ColorRule | null;
  /**
   * 凡例ボタンの縦位置 Tailwind クラス (既定 'bottom-20')。
   * MapPage は BottomTabBar (pb-16) を避けるため既定の 'bottom-20'、分析地図はタブバーが無いので
   * 'bottom-2' で地図下端に揃える (中途半端な浮きを解消)。
   */
  bottomClassName?: string;
}

function legendEntriesFallback(mode: DistrictMode | null | undefined): MapLegendEntry[] {
  const label = mapModeLabel(mode);
  if (!label) return [];
  return [{ color: defaultMarkerColorForMode(mode), label }];
}

export function MapLegend({
  mode,
  colorRule = null,
  bottomClassName = 'bottom-20',
}: MapLegendProps) {
  const [open, setOpen] = useState(false);

  // Sprint BG-DataSettings 6+7: useEcwLegend が colorRule あれば buckets/cells + fallback を、
  // なければ mode-default 1 件を返す。null 安定化のため inline fallback も用意。
  const ecwEntries = useEcwLegend(colorRule, mode);
  const entries = ecwEntries.length > 0 ? ecwEntries : legendEntriesFallback(mode);
  if (entries.length === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="凡例を開く"
        aria-expanded={open}
        // 縦位置は bottomClassName (既定 bottom-20=BottomTabBar 回避)。分析地図は bottom-2 で下端揃え。
        // z-30: お絵描き canvas (z-20) より上に置き、メモ描画中でも凡例を開けるようにする。
        className={`absolute ${bottomClassName} left-2 z-30 bg-white/90 rounded shadow px-2 py-1.5 text-xs text-primary-700 hover:text-primary-900 border border-slate-200 min-h-touch`}
      >
        凡例
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="凡例"
          aria-modal="true"
          onClick={() => setOpen(false)}
          className="fixed inset-0 bg-black/30 z-40 flex items-end sm:items-center justify-center"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-t-xl sm:rounded-xl p-4 w-full sm:max-w-xs max-h-[60vh] overflow-auto shadow-lg"
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium text-sm text-slate-900">凡例</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="凡例を閉じる"
                className="text-slate-400 hover:text-slate-700 bg-transparent border-none p-0 cursor-pointer text-lg leading-none"
              >
                &times;
              </button>
            </div>
            <ul className="flex flex-col gap-2 list-none p-0 m-0">
              {entries.map((e) => (
                <li key={e.label} className="flex items-center gap-2">
                  {e.icon ? (
                    // 地図マーカーと一致させるため ColorRule 行の icon を entry 色で表示。
                    <span
                      className="inline-flex w-5 h-5 items-center justify-center shrink-0 text-base leading-none"
                      style={{ color: e.color }}
                      aria-hidden="true"
                      data-testid="map-legend-icon"
                    >
                      {e.icon}
                    </span>
                  ) : (
                    <span
                      className="inline-block w-4 h-4 rounded-full shrink-0 border border-slate-200"
                      style={{ backgroundColor: e.color }}
                      aria-hidden="true"
                      data-testid="map-legend-color"
                    />
                  )}
                  <span className="text-sm text-slate-700">{e.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
